import type {CustomPropertiesSchemaPagePayload, PropertyDefinition} from '@github-ui/custom-properties-types'
import {ListItem, ListView} from '@github-ui/list-view'
import {customPropertyDetailsPath} from '@github-ui/paths'
import {Link} from '@github-ui/react-core/link'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {TrashIcon} from '@primer/octicons-react'
import {ActionList, Box, Label, Text} from '@primer/react'
import {createRef, useState} from 'react'

import {useCurrentOrg} from '../contexts/CurrentOrgRepoContext'
import {definitionTypeLabels} from '../helpers/definition-type-labels'
import {DeleteDefinitionDialog} from './DeleteDefinitionDialog'
import {EmptyState} from './EmptyState'
import {PropertiesHeaderPageTabs} from './PropertiesHeaderPageTabs'

export function DefinitionsPage() {
  const {definitions, permissions} = useRoutePayload<CustomPropertiesSchemaPagePayload>()

  return (
    <>
      <PropertiesHeaderPageTabs permissions={permissions} definitions={definitions} />
      <DefinitionsList definitions={definitions} />
    </>
  )
}

function DefinitionsList({definitions}: {definitions: PropertyDefinition[]}) {
  if (definitions.length === 0) {
    return <EmptyState>No properties have been added</EmptyState>
  }

  return (
    <Box
      data-hpc
      sx={{border: '1px solid', borderColor: 'border.muted', borderRadius: 2}}
      data-testid="repos-definitions-list"
    >
      <ListView
        title="Property definitions"
        metadata={
          <ListView.Metadata
            title={
              <Text sx={{fontWeight: 'bold'}}>
                {definitions.length} {definitions.length === 1 ? 'property' : 'properties'}
              </Text>
            }
          />
        }
      >
        {definitions.map(definition => (
          <DefinitionListItem key={definition.propertyName} definition={definition} />
        ))}
      </ListView>
    </Box>
  )
}

interface DefinitionListItemProps {
  definition: PropertyDefinition
}

function DefinitionListItem({definition}: DefinitionListItemProps) {
  const [deleteDialogIsOpen, setDeleteDialogIsOpen] = useState(false)
  const org = useCurrentOrg()

  const contextMenuButtonRef = createRef<HTMLButtonElement>()

  function closeDeleteDialog() {
    setDeleteDialogIsOpen(false)
    contextMenuButtonRef.current?.focus()
  }

  return (
    <>
      <ListItem
        sx={{gap: 0, pl: 2}}
        title={
          <ListItem.Title
            value={definition.propertyName}
            containerSx={{
              display: 'flex',
              flexFlow: 'row',
              alignItems: 'baseline',
              mx: 2,
            }}
            href="#"
            linkProps={{
              as: Link,
              to: customPropertyDetailsPath({org: org.login, propertyName: definition.propertyName}),
            }}
          >
            {definition.required && (
              <Label variant="secondary" sx={{ml: 2}}>
                Required
              </Label>
            )}
          </ListItem.Title>
        }
        metadata={
          <ListItem.Metadata variant="secondary" alignment="right" sx={{pl: 1, width: '82px'}}>
            <Text sx={{display: ['none', 'flex'], whiteSpace: 'nowrap'}}>
              {definitionTypeLabels[definition.valueType] || ''}
            </Text>
          </ListItem.Metadata>
        }
        metadataContainerSx={{pr: 0}}
        secondaryActions={
          <ListItem.ActionBar
            anchorRef={contextMenuButtonRef}
            staticMenuActions={[
              {
                key: 'delete',
                render: () => (
                  <ActionList.Item variant="danger" onSelect={() => setDeleteDialogIsOpen(true)}>
                    <ActionList.LeadingVisual>
                      <TrashIcon />
                    </ActionList.LeadingVisual>
                    Delete
                  </ActionList.Item>
                ),
              },
            ]}
          />
        }
      >
        <ListItem.MainContent>
          {definition.description && (
            <ListItem.Description
              sx={{
                minWidth: 0,
                maxWidth: '100%',
                display: 'flex',
              }}
            >
              <ListItem.DescriptionItem
                sx={{
                  display: 'block',
                  ml: 2,
                }}
              >
                {definition.description}
              </ListItem.DescriptionItem>
            </ListItem.Description>
          )}
        </ListItem.MainContent>
      </ListItem>

      {deleteDialogIsOpen && (
        <DeleteDefinitionDialog definition={definition} onCancel={closeDeleteDialog} onDismiss={closeDeleteDialog} />
      )}
    </>
  )
}

try{ DefinitionsPage.displayName ||= 'DefinitionsPage' } catch {}
try{ DefinitionsList.displayName ||= 'DefinitionsList' } catch {}
try{ DefinitionListItem.displayName ||= 'DefinitionListItem' } catch {}